import {
  ApiError,
  ApiErrorInitialState,
} from '@hellodarwin/core/lib/features/entities/api-entitites';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { RootState } from '../../../app/app-store';
import ClientApiV2 from '../client-api-v2';

export type ClientAIApiKeyStatus = 'active' | 'revoked';

export interface ClientAIApiKey {
  id: string;
  name: string;
  status: ClientAIApiKeyStatus;
  masked_key: string;
  secret_key?: string;
  last_used_at?: string | null;
  created_at: string;
  updated_at: string;
  created_by_name: string;
}

interface AIIntegrationState {
  keys: ClientAIApiKey[];
  status: 'idle' | 'pending';
  error: ApiError;
}

const initialState: AIIntegrationState = {
  keys: [],
  status: 'idle',
  error: ApiErrorInitialState,
};

export const fetchClientAIApiKeys = createAsyncThunk<
  ClientAIApiKey[],
  { api: ClientApiV2 },
  { rejectValue: ApiError }
>('client/aiIntegration/fetchKeys', async ({ api }, { rejectWithValue }) => {
  try {
    const response = await api.get<ClientAIApiKey[]>('/ai-integration/api-keys');
    return response.data;
  } catch (err: any) {
    return rejectWithValue(err.response?.data ?? ApiErrorInitialState);
  }
});

export const createClientAIApiKey = createAsyncThunk<
  ClientAIApiKey,
  { api: ClientApiV2; name?: string },
  { rejectValue: ApiError }
>(
  'client/aiIntegration/createKey',
  async ({ api, name }, { rejectWithValue }) => {
    try {
      const response = await api.post<ClientAIApiKey>(
        '/ai-integration/api-keys',
        name ? { name } : {},
      );
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data ?? ApiErrorInitialState);
    }
  },
);

export const revokeClientAIApiKey = createAsyncThunk<
  string,
  { api: ClientApiV2; apiKeyId: string },
  { rejectValue: ApiError }
>(
  'client/aiIntegration/revokeKey',
  async ({ api, apiKeyId }, { rejectWithValue }) => {
    try {
      await api.delete(`/ai-integration/api-keys/${apiKeyId}`);
      return apiKeyId;
    } catch (err: any) {
      return rejectWithValue(err.response?.data ?? ApiErrorInitialState);
    }
  },
);

const aiIntegrationSlice = createSlice({
  name: 'aiIntegration',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(fetchClientAIApiKeys.pending, (state) => {
      state.status = 'pending';
    });
    builder.addCase(fetchClientAIApiKeys.fulfilled, (state, { payload }) => {
      state.keys = payload;
      state.status = 'idle';
    });
    builder.addCase(fetchClientAIApiKeys.rejected, (state, { payload }) => {
      state.error = payload ?? ApiErrorInitialState;
      state.status = 'idle';
    });

    builder.addCase(createClientAIApiKey.pending, (state) => {
      state.status = 'pending';
    });
    builder.addCase(createClientAIApiKey.fulfilled, (state, { payload }) => {
      state.keys = [payload, ...state.keys.filter((key) => key.id !== payload.id)];
      state.status = 'idle';
    });
    builder.addCase(createClientAIApiKey.rejected, (state, { payload }) => {
      state.error = payload ?? ApiErrorInitialState;
      state.status = 'idle';
    });

    builder.addCase(revokeClientAIApiKey.pending, (state) => {
      state.status = 'pending';
    });
    builder.addCase(revokeClientAIApiKey.fulfilled, (state, { payload }) => {
      state.keys = state.keys.map((key) =>
        key.id === payload
          ? {
              ...key,
              status: 'revoked',
            }
          : key,
      );
      state.status = 'idle';
    });
    builder.addCase(revokeClientAIApiKey.rejected, (state, { payload }) => {
      state.error = payload ?? ApiErrorInitialState;
      state.status = 'idle';
    });
  },
});

export const aiIntegrationReducer = aiIntegrationSlice.reducer;

export const selectClientAIApiKeys = (state: RootState) =>
  state.aiIntegration.keys;

export const selectClientAIApiKeysStatus = (state: RootState) =>
  state.aiIntegration.status;
